import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { Share2Icon } from '@air/design-icons';
import { CanvasTopBarViewOnlyNotice } from '@air/feature-edit-canvas/canvas-top-bar-view-only-notice';
import { Button } from '@air/primitive-button';
import { ONE_SECOND } from '@air/utilities';

export type PublicCanvasTopBarProps = {
  title: string;
};

export const PUBLIC_CANVAS_COPIED_FEEDBACK_DURATION_MS = 2 * ONE_SECOND;

export const PublicCanvasTopBar = memo(({ title }: PublicCanvasTopBarProps) => {
  const [viewOnlyDismissed, setViewOnlyDismissed] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimerIdRef = useRef<number | undefined>(undefined);

  const handleDismissViewOnly = useCallback(() => setViewOnlyDismissed(true), []);

  const handleShare = useCallback(() => {
    void navigator.clipboard.writeText(window.location.href);
    setCopied(true);

    if (copiedTimerIdRef.current !== undefined) {
      window.clearTimeout(copiedTimerIdRef.current);
    }

    copiedTimerIdRef.current = window.setTimeout(() => {
      setCopied(false);
      copiedTimerIdRef.current = undefined;
    }, PUBLIC_CANVAS_COPIED_FEEDBACK_DURATION_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (copiedTimerIdRef.current !== undefined) {
        window.clearTimeout(copiedTimerIdRef.current);
      }
    };
  }, []);

  return (
    <div className="flex h-14 items-center justify-between gap-2 px-2">
      <div className="pointer-events-auto min-w-0 rounded-8 bg-gray-1 px-2 py-1.5">
        <span className="block truncate px-2 py-1 font-semibold text-12 text-gray-12" data-testid="PUBLIC_CANVAS_TITLE">
          {title}
        </span>
      </div>
      {!viewOnlyDismissed && <CanvasTopBarViewOnlyNotice onDismiss={handleDismissViewOnly} />}
      <div className="pointer-events-auto shrink-0 rounded-8 bg-gray-1 px-2 py-1.5">
        <Button
          color="gray"
          data-testid="PUBLIC_CANVAS_SHARE_BUTTON"
          onClick={handleShare}
          prefix={<Share2Icon aria-hidden size="sm" />}
          size="medium"
        >
          {copied ? 'Copied!' : 'Share'}
        </Button>
      </div>
    </div>
  );
});

PublicCanvasTopBar.displayName = 'PublicCanvasTopBar';
